import { useEffect, useState } from "react";
import { Input, Spin } from "antd";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Mainlayout from "../../layout/MainLayout";
import { Button1, Checkbox, Select } from "../index";
import copyIcon from "../../assets/icons/copy-07.png";
import { moneyRequestsApi } from "../../api/endpoints";
import { useAuth } from "../../api/useAuth";
import QuickActionSelect from "../quick-action-select/QuickActionSelect";

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" }) : "-";

const RequestMoneySummary = () => {
  const navigate = useNavigate();
  const { state: routeState } = useLocation();
  const [params] = useSearchParams();
  const { user } = useAuth();

  const idFromUrl = params.get("id");
  const idFromState = routeState?.requestId;
  const requestId = idFromUrl || idFromState;
  const seedFromState = routeState?.request;

  const [loading, setLoading] = useState(!seedFromState);
  const [request, setRequest] = useState(seedFromState || null);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (!requestId || seedFromState) return;
    setLoading(true);
    moneyRequestsApi
      .getById(requestId)
      .then((data) => {
        setRequest(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.response?.data?.error || "Failed to load request");
        setLoading(false);
      });
  }, [requestId, seedFromState]);

  const handleCancel = async () => {
    if (!request?.id) return navigate("/request-money");
    setCancelling(true);
    try {
      await moneyRequestsApi.updateStatus(request.id, "CANCELLED");
      navigate("/request-money");
    } catch (e) {
      setError(e.response?.data?.error || "Failed to cancel request");
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <Mainlayout NavName="Request Money" expand={false}>
        <div className="d-flex justify-content-center py-5">
          <Spin size="large" />
        </div>
      </Mainlayout>
    );
  }

  const userName = user
    ? `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email || "-"
    : "-";

  const summaryData = [
    { name: "Date", info: fmtDate(request?.createdAt) },
    { name: "Created by", info: userName },
    { name: "Reference", info: request?.reference || request?.id?.slice(0, 8) || "-" },
    { name: "Status", info: request?.status || "PENDING" },
    { name: "Payer", info: request?.payerEmail || "-" },
    { name: "Note", info: request?.note || "-" },
  ];

  const amountStr = request
    ? `${Number(request.amount || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} ${request.currency || "AUD"}`
    : "-";

  const shareLink = request
    ? `${window.location.origin}/pay/${request.reference || request.id}`
    : "";

  return (
    <Mainlayout NavName="Request Money" expand={false}>
      <div className="container mt-5">
        <div className="d-flex align-items-center justify-content-between">
          <span className="fs-5 fw-semibold">Summary</span>
          <QuickActionSelect />
        </div>

        {error && (
          <div className="mt-3" style={{ color: "#FC555B" }}>
            {error}
          </div>
        )}

        {!request ? (
          <div
            className="text-center py-5"
            style={{ color: "var(--text-muted)" }}
          >
            No request to display. Create a request first or pass an id in the
            URL.
          </div>
        ) : (
          <div className="row border justify-content-between gap-4 rounded-3 p-3 mt-3 mx-1">
            <div className="d-flex flex-column gap-1">
              <span className="fs-5 fw-medium">{amountStr}</span>
              <span className="fs12px" style={{ color: "#7D7D7D" }}>
                Created {fmtDate(request.createdAt)}
              </span>
            </div>

            <div className="d-flex flex-wrap gap-4 justify-content-between align-items-center">
              {summaryData.map(({ name, info }) => (
                <div
                  key={name}
                  className="d-flex flex-column gap-1"
                  style={{ width: "45%" }}
                >
                  <span className="fs12px" style={{ color: "#7D7D7D" }}>
                    {name}
                  </span>
                  <span className="fs14px fw-medium">{info}</span>
                </div>
              ))}
            </div>

            <div className="d-flex align-items-center flex-wrap justify-content-between">
              <div
                className="d-flex flex-column gap-2"
                style={{ width: "45%" }}
              >
                <label className="fs12px fw-medium">Methods</label>
                <Input className="p-3" value="Link, QR, Email" disabled />
              </div>
              <div
                className="d-flex flex-column gap-2"
                style={{ width: "45%" }}
              >
                <label className="fs12px fw-medium">Link</label>
                <Input
                  className="p-3"
                  value={shareLink}
                  readOnly
                  suffix={
                    <img
                      src={copyIcon}
                      alt="copy"
                      className="cursor-pointer"
                      onClick={() => navigator.clipboard?.writeText(shareLink)}
                    />
                  }
                />
              </div>
            </div>

            <div>
              <Checkbox>Show QR Code</Checkbox>
            </div>

            <div className="d-flex justify-content-md-end justify-content-center gap-3 flex-md-nowrap flex-wrap align-items-center">
              <Button1 buttonName={"Send QR Code"} className={"border"} />
              <Button1
                buttonName={"Send Email"}
                className={"btn-outline-success"}
              />
              <Button1
                buttonName={"Share Link"}
                className={"btn-dark"}
                onClick={() => navigator.clipboard?.writeText(shareLink)}
              />
              <Button1
                buttonName={cancelling ? "Cancelling..." : "Cancel Request"}
                className={"btn-outline-danger"}
                onClick={handleCancel}
                disabled={cancelling || request.status === "CANCELLED"}
              />
            </div>
          </div>
        )}
      </div>
    </Mainlayout>
  );
};

export default RequestMoneySummary;
